import { useNavigate, useParams } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { isAxiosError } from 'axios'
import { ChallengeService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { trackAchievement, useTar } from '@/modules'
import { useAuth } from '@/hooks/useAuth'
import { useEffectAsync } from '@/hooks/useEffectAsync'
import { LoginRequiredEmptyState, NoDataEmptyState } from '../EmptyStates'
import { StandardLoader } from '../StandardLoader'
import {
  VisibilityRail,
  PublicLinkRail,
  ShareSettingsPatch,
} from './ShareSettingsPanel'
import { SharePageAnalytics } from './SharePageAnalytics'
import { ProofOfAchievement } from './ProofOfAchievement'

type LoadError = 'not-found' | 'failed'

export const ShareAchievementPage = () => {
  const { t } = useTar()
  const navigate = useNavigate()
  const { challengeSlug } = useParams({ from: '/my-shares/$challengeSlug' })
  const { isAuthenticated, isInitializing } = useAuth()
  const [achievement, setAchievement] =
    useState<ChallengeService.Achievement | null>(null)
  const [analytics, setAnalytics] =
    useState<ChallengeService.AchievementAnalytics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<LoadError | null>(null)

  useEffectAsync(async () => {
    if (!isAuthenticated) {
      if (!isInitializing) setIsLoading(false)
      return
    }
    try {
      setIsLoading(true)
      setLoadError(null)
      const [{ data: achievementData }, { data: analyticsData }] =
        await Promise.all([
          apis.challengeService.getAchievementByChallenge(challengeSlug),
          apis.challengeService.getAchievementAnalytics(challengeSlug),
        ])
      setAchievement(achievementData)
      setAnalytics(analyticsData)
      trackAchievement('Achievement Manage Page Viewed', {
        challengeSlug,
        slug: achievementData.slug,
        isPublic: achievementData.isPublic,
        hasBeenPublished: !!achievementData.firstPublishedAt,
        taskName: achievementData.taskName,
        attemptNumber: achievementData.attemptNumber,
        totalViews: analyticsData.totalViews,
      })
    } catch (error) {
      console.error('Failed to load achievement:', error)
      setLoadError(
        isAxiosError(error) && error.response?.status === 404
          ? 'not-found'
          : 'failed'
      )
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, isInitializing, challengeSlug])

  const publicUrl = useMemo(
    () =>
      achievement
        ? `${window.location.origin}/submission/${achievement.slug}`
        : '',
    [achievement]
  )

  const onPatch = (patch: ShareSettingsPatch) => {
    if (!achievement) return
    const previous = achievement
    const next = { ...achievement, ...patch }
    if (patch.isPublic === true && !previous.firstPublishedAt) {
      next.firstPublishedAt = new Date().toISOString()
    }
    setAchievement(next)

    if (patch.isPublic === true && !previous.isPublic) {
      trackAchievement('Achievement Shared', {
        challengeSlug,
        slug: previous.slug,
        taskName: previous.taskName,
        attemptNumber: previous.attemptNumber,
        grade: previous.grade,
        weaponOfChoice: previous.weaponOfChoice,
        // First publish ever vs. re-publishing a previously hidden page.
        isFirstPublish: !previous.firstPublishedAt,
      })
    } else if (patch.isPublic === false && previous.isPublic) {
      trackAchievement('Achievement Hidden', {
        challengeSlug,
        slug: previous.slug,
      })
    } else {
      const changedFields = Object.keys(patch)
      trackAchievement('Achievement Settings Changed', {
        challengeSlug,
        slug: previous.slug,
        changedFields,
        // Single-toggle case (the common one): surface which field and its new
        // value so the metric is queryable without unpacking an array.
        changedField: changedFields.length === 1 ? changedFields[0] : undefined,
        newValue:
          changedFields.length === 1
            ? (patch as Record<string, unknown>)[changedFields[0]]
            : undefined,
      })
    }

    apis.challengeService
      .updateAchievementByChallenge(challengeSlug, patch)
      .then(({ data }) => {
        setAchievement((current) =>
          current && current.slug === data.slug ? data : current
        )
      })
      .catch((error) => {
        console.error('Failed to save share setting:', error)
        setAchievement(previous)
        toast.error(t('share.errors.saveFailed'))
      })
  }

  if (!isAuthenticated && !isInitializing) {
    return (
      <div className="flex flex-1 w-full items-center justify-center">
        <LoginRequiredEmptyState />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="flex flex-1 w-full items-center justify-center">
        <NoDataEmptyState
          title={t(
            loadError === 'not-found'
              ? 'share.errors.notFoundTitle'
              : 'share.errors.loadFailedTitle'
          )}
          description={t(
            loadError === 'not-found'
              ? 'share.errors.notFoundDescription'
              : 'share.errors.loadFailedDescription'
          )}
          buttonText={t('share.errors.backToCompleted')}
          onButtonClick={() => navigate({ to: '/challenges/completed' })}
        />
      </div>
    )
  }

  return (
    <div className="relative max-w-[1340px] mx-auto px-2 md:px-7 py-8 pb-20">
      {/* Top crumbs + status tag */}
      <div className="flex items-center justify-end">
        {achievement?.isPublic ? (
          <span className="inline-flex items-center gap-2 font-jet-mono text-[10px] tracking-[0.22em] uppercase text-primary border border-primary/30 bg-primary/10 rounded-full px-2.5 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--color-primary))]" />
            {t('share.toggle.live')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 font-jet-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground border border-border rounded-full px-2.5 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60" />
            {t('share.toggle.private')}
          </span>
        )}
      </div>

      {/* Editorial headline */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-end gap-8 mb-16">
        <h1 className="font-fraunces font-light text-[clamp(32px,4vw,56px)] leading-[1.05] tracking-[-0.025em] text-foreground">
          {t('share.page.title')}
        </h1>
        <p className="max-w-[340px] text-sm leading-relaxed text-muted-foreground border-l border-border pl-4">
          {t('share.page.sub')}
        </p>
      </div>

      {isAuthenticated && achievement && analytics && (
        <>
          {/*
            Stage layout
              • mobile (<md): rails stack above preview, one after the other
              • md & lg (≥768px, <1400px): rails sit side-by-side above the preview
              • 2xl (≥1400px): rails float absolutely over the preview's left/right corners
                (the rails-wrapper becomes display:contents so its grid/margins drop out)
          */}
          <div className="relative mb-20 max-w-[720px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-8 showcase:contents">
              <aside className="showcase:absolute showcase:top-[30px] showcase:-left-[200px] showcase:w-[280px] showcase:z-10 showcase:drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]">
                <VisibilityRail
                  showPercentile={achievement.showPercentile}
                  showWorkTime={achievement.showWorkTime}
                  showMemberSince={achievement.showMemberSince}
                  showFeedbackExcerpt={achievement.showFeedbackExcerpt}
                  percentileAvailable={achievement.percentileAvailable}
                  onPatch={onPatch}
                />
              </aside>

              <aside className="showcase:absolute showcase:top-[30px] showcase:-right-[200px] showcase:w-[280px] showcase:z-10 showcase:drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]">
                <PublicLinkRail
                  publicUrl={publicUrl}
                  isPublic={achievement.isPublic}
                  challengeSlug={challengeSlug}
                  slug={achievement.slug}
                  onPatch={onPatch}
                />
              </aside>
            </div>

            <section className="relative">
              <ProofOfAchievement
                name={achievement.name}
                taskName={achievement.taskName}
                attemptNumber={achievement.attemptNumber}
                grade={achievement.grade}
                submittedAt={new Date(achievement.submittedAt)}
                percentile={
                  achievement.showPercentile ? achievement.percentile : null
                }
                workTime={
                  achievement.showWorkTime ? achievement.workTime : null
                }
                memberSince={
                  achievement.showMemberSince && achievement.memberSince
                    ? new Date(achievement.memberSince)
                    : null
                }
                feedbackExcerpt={
                  achievement.showFeedbackExcerpt
                    ? achievement.feedbackExcerpt
                    : null
                }
                weaponOfChoice={achievement.weaponOfChoice}
                organization={achievement.organization}
              />
            </section>
          </div>

          <SharePageAnalytics
            data={analytics}
            hasBeenPublished={!!achievement.firstPublishedAt}
          />
        </>
      )}

      {isAuthenticated && isLoading && (
        <div className="flex justify-center py-12">
          <StandardLoader size="lg" />
        </div>
      )}
    </div>
  )
}
